/**
 * Read a boolean feature flag from a `REACT_APP_*` build variable.
 *
 * Exists because CRA bakes `process.env.REACT_APP_*` in at build time, and SEPT ships a separate
 * frontend build per client — so a per-build variable is how one client gets a feature and another
 * does not, without branching the code.
 *
 * `Boolean(process.env.X)` is the wrong way to do this and is the usual way this bug ships:
 * env values are always strings, so `Boolean('false')` is `true` and a client who explicitly
 * disabled a feature would get it anyway. This parses the string.
 *
 * Accepts `true/1/yes/on` and `false/0/no/off`, case- and whitespace-insensitive (an env var set
 * from a CI variable or a shell heredoc picks up stray whitespace surprisingly often). Anything
 * else falls back and WARNS — unconditionally, including in production.
 *
 * `toEnvironmentId` next door suppresses its warning outside development, and that is wrong for
 * this one: a client build IS a production build, so `REACT_APP_FEATURE_BIDS=ture` would silently
 * produce a missing feature with no diagnostic anywhere in the artifact. A typo in a deployment
 * variable is exactly when someone needs to be told, and the cost is one console line in the rare
 * case where a value is set but unparseable.
 */
const TRUTHY = new Set(['true', '1', 'yes', 'on']);
const FALSY = new Set(['false', '0', 'no', 'off']);

export const flagFromEnv = (
  name: string,
  rawValue: string | undefined,
  fallback: boolean,
): boolean => {
  if (rawValue === undefined || rawValue === null) {
    return fallback;
  }
  // Defensive: TS says string, but this runs at module load from a build-injected value, and a
  // throw here is a white screen rather than a caught error.
  if (typeof rawValue !== 'string') {
    console.warn(`Non-string value for ${name}; falling back to ${fallback}.`);
    return fallback;
  }
  if (rawValue.trim() === '') {
    return fallback;
  }

  const normalised = rawValue.trim().toLowerCase();
  if (TRUTHY.has(normalised)) return true;
  if (FALSY.has(normalised)) return false;

  console.warn(
    `Unrecognised boolean for ${name}: "${rawValue}". Expected one of ` +
      `${[...TRUTHY].join('/')} or ${[...FALSY].join('/')}; falling back to ${fallback}.`,
  );
  return fallback;
};
